import { c as _c } from "react/compiler-runtime";
import type { Textarea } from '@/components/ui/textarea';
import { BoldIcon, ItalicIcon, StrikethroughIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCommentEditor } from '@/hooks/use-comment-editor';
import { Markdown } from '../mdx/markdown';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from '../ui/input-group';
type CommentEditorProps = {
  tabsValue?: string;
  onTabsValueChange?: (value: string) => void;
  onModEnter?: () => void;
  onEscape?: () => void;
} & React.ComponentProps<typeof Textarea>;
export function CommentEditor(props) {
  const $ = _c(72);
  let onEscape;
  let onModEnter;
  let onTabsValueChange;
  let rest;
  let tabsValue;
  let value;
  if ($[0] !== props) {
    ({
      value,
      tabsValue,
      onTabsValueChange,
      onModEnter,
      onEscape,
      ...rest
    } = props);
    $[0] = props;
    $[1] = onEscape;
    $[2] = onModEnter;
    $[3] = onTabsValueChange;
    $[4] = rest;
    $[5] = tabsValue;
    $[6] = value;
  } else {
    onEscape = $[1];
    onModEnter = $[2];
    onTabsValueChange = $[3];
    rest = $[4];
    tabsValue = $[5];
    value = $[6];
  }
  const t = useTranslations();
  let t0;
  if ($[7] !== onEscape || $[8] !== onModEnter) {
    t0 = {
      onModEnter,
      onEscape
    };
    $[7] = onEscape;
    $[8] = onModEnter;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  const {
    textareaRef,
    handleKeyDown,
    handleInput,
    handleCompositionStart,
    handleCompositionEnd,
    decorateText
  } = useCommentEditor(t0);
  const t1 = tabsValue ?? "write";
  let t2;
  if ($[10] !== t) {
    t2 = t("blog.comments.write");
    $[10] = t;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t2) {
    t3 = <TabsTrigger value="write">{t2}</TabsTrigger>;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t) {
    t4 = t("blog.comments.preview");
    $[14] = t;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t4) {
    t5 = <TabsTrigger value="preview">{t4}</TabsTrigger>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t3 || $[19] !== t5) {
    t6 = <TabsList>{t3}{t5}</TabsList>;
    $[18] = t3;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== handleCompositionEnd || $[22] !== handleCompositionStart || $[23] !== handleInput || $[24] !== handleKeyDown || $[25] !== rest || $[26] !== textareaRef || $[27] !== value) {
    t7 = <InputGroupTextarea rows={1} value={value} onKeyDown={handleKeyDown} onInput={handleInput} onCompositionStart={handleCompositionStart} onCompositionEnd={handleCompositionEnd} ref={textareaRef} className="min-h-10 resize-none font-mono" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" data-testid="comment-editor-textarea" {...rest} />;
    $[21] = handleCompositionEnd;
    $[22] = handleCompositionStart;
    $[23] = handleInput;
    $[24] = handleKeyDown;
    $[25] = rest;
    $[26] = textareaRef;
    $[27] = value;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t) {
    t8 = t("blog.comments.toggle-bold");
    $[29] = t;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== decorateText) {
    t9 = () => {
      decorateText("bold");
    };
    $[31] = decorateText;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <BoldIcon />;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t8 || $[35] !== t9) {
    t11 = <InputGroupButton aria-label={t8} variant="ghost" size="icon-xs" onClick={t9}>{t10}</InputGroupButton>;
    $[34] = t8;
    $[35] = t9;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t) {
    t12 = t("blog.comments.toggle-strikethrough");
    $[37] = t;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== decorateText) {
    t13 = () => {
      decorateText("strikethrough");
    };
    $[39] = decorateText;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <StrikethroughIcon />;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t12 || $[43] !== t13) {
    t15 = <InputGroupButton aria-label={t12} variant="ghost" size="icon-xs" onClick={t13}>{t14}</InputGroupButton>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t) {
    t16 = t("blog.comments.toggle-italic");
    $[45] = t;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== decorateText) {
    t17 = () => {
      decorateText("italic");
    };
    $[47] = decorateText;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ItalicIcon />;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t16 || $[51] !== t17) {
    t19 = <InputGroupButton aria-label={t16} variant="ghost" size="icon-xs" onClick={t17}>{t18}</InputGroupButton>;
    $[50] = t16;
    $[51] = t17;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t11 || $[54] !== t15 || $[55] !== t19) {
    t20 = <InputGroupAddon align="block-end">{t11}{t15}{t19}</InputGroupAddon>;
    $[53] = t11;
    $[54] = t15;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t20 || $[58] !== t7) {
    t21 = <TabsContent value="write"><InputGroup>{t7}{t20}</InputGroup></TabsContent>;
    $[57] = t20;
    $[58] = t7;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t || $[61] !== value) {
    t22 = typeof value === "string" && value.trim() !== "" ? value : t("blog.comments.nothing-to-preview");
    $[60] = t;
    $[61] = value;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t22) {
    t23 = <TabsContent value="preview" className="rounded-md border border-input px-2.5 dark:bg-input/30"><Markdown>{t22}</Markdown></TabsContent>;
    $[63] = t22;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== onTabsValueChange || $[66] !== t1 || $[67] !== t21 || $[68] !== t23 || $[69] !== t6 || $[70] !== tabsValue) {
    t24 = <Tabs value={tabsValue} onValueChange={onTabsValueChange} defaultValue={t1}>{t6}{t21}{t23}</Tabs>;
    $[65] = onTabsValueChange;
    $[66] = t1;
    $[67] = t21;
    $[68] = t23;
    $[69] = t6;
    $[70] = tabsValue;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  return t24;
}